<template>
	<div class="timeline-container" v-loading="isLoading">
		<iframe ref="iframeRef" style="width: 100%" src="/timeline.html"></iframe>
	</div>
</template>

<script lang="ts">
import { defineComponent, ref, watch, onUnmounted, nextTick } from 'vue';
import EmployeesJobsScheduleApiHelper from '@/helpers/api/EmployeesJobsScheduleApiHelper';
import EmployeeJobScheduleModel from '@/models/EmployeeJobScheduleModel';
import TimelineGroupModel from './models/TimelineGroupModel';
import TimelineItemModel from './models/TimelineItemModel';
import TimelineConfiguration from './models/TimelineConfigurationModel';
import ITimelineEvent from './models/ITimelineEvent';
import dateOnlyHelper from '@/helpers/dateOnlyHelper';
import ITimelineItem from './models/ITimelineItem';
import timeOnlyHelper from '@/helpers/timeOnlyHelper';
import EmployeeModel from '@/models/EmployeeModel';
import OrderModel from '@/models/OrderModel';
import OrderJobModel from '@/models/OrderJobModel';
import { ElNotification } from 'element-plus';
import store from '@/store';

export default defineComponent({
	props: {		
		visible: {
			type: Boolean,
			default: false
		},
		branch: {
			type: Number,
			default: 0
		},
		scheduleDate: {
			type: Date,
			default: null
		},		
		schedule: {
			type: Array,
			default: null
		}
	},
	emits: ['item-updated', 'item-clicked'],
	setup(props, { emit }) {
		// Constants
		const jobsScheduleApiHelper = new EmployeesJobsScheduleApiHelper();

		// Reactive state
		const isLoading = ref(false);
		const isInitialized = ref(false);
		const isIframeVisible = ref(false);
		const iframeRef = ref<HTMLIFrameElement | null>(null);
		const iframeState = ref<{
			status: 'hidden' | 'loading' | 'loaded' | 'error';
			error: string | null;
		}>({ status: 'hidden', error: null });

		// Watchers
		watch(
			() => props.visible, 
			(visible: boolean) => {
				if (visible) {					
					nextTick().then(() => initTimelineIframe());
				} else {
					destroyTimelineIframe();
					removeTimelineEventListener();
				}
			}
		);

		// Lifecycle events
		onUnmounted(() => {
			destroyTimelineIframe();
			removeTimelineEventListener();
		});

		// Methods		
		const initTimelineIframe = async () => {
			if (isInitialized.value) {
				return;
			}

			try {
				isInitialized.value = true;
				iframeState.value = { status: 'loading', error: null };
				addTimelineEventListener();
	
				const employeesJobsSchedule = props.schedule as Array<EmployeeJobScheduleModel>;
				let jobsSchedule: Array<TimelineGroupModel> = [];

				if (employeesJobsSchedule?.length) {						
					employeesJobsSchedule.forEach((j, eIndex) => {
						if (!j?.employee?.id) {
							return;
						}

						const employeeId = j.employee!.id;
						const existingEmployee = jobsSchedule.find(_ => _.id == employeeId);

						/** an item of jobsSchedule array */
						let timelineGroup: TimelineGroupModel;

						if (!existingEmployee) {		
							timelineGroup = new TimelineGroupModel();				
							timelineGroup.id = j.employee!.id;
							// timelineGroup.props.type = 'Employee';
							// timelineGroup.props.typeId = j.employee!.id;
							timelineGroup.name = `${j.employee!.lastname} ${j.employee!.firstname}`,								
							timelineGroup.head = true;
							jobsSchedule.push(timelineGroup);
						} else {
							timelineGroup = existingEmployee;
						}

						// employee => order
						// TODO: here could be subscontractor instead of employee
						const employeeOrders = employeesJobsSchedule.filter(_ => _?.employee?.id == employeeId);

						if (!employeeOrders.length) {
							return;
						}

						timelineGroup.subGroups = [];

						employeeOrders.forEach((o, oIndex) => {
							if (!o.order) {
								return;
							}

							const orderId = o.order!.id;
							let timelineOrderGroup: TimelineGroupModel;
							const existingOrder = timelineGroup.subGroups?.find(_ => _.props?.id == orderId);

							if (!existingOrder) {
								const orderGovNumber = o.order.vehicle?.govermentalNumber ?? '';
								timelineOrderGroup = new TimelineGroupModel();
								timelineOrderGroup.id = (orderId + 1) * Math.sqrt(employeeId + eIndex + oIndex);
								// timelineOrderGroup.props.type = 'Order';
								timelineOrderGroup.props = { id: orderId };
								timelineOrderGroup.name = `${orderGovNumber}${orderGovNumber.length ? ', ' : ''} ${o.order!.vehicle!.model!.manufacturer!.name} ${o.order!.vehicle!.model!.name}`;
								
								// add order to chart-data
								timelineGroup.subGroups!.push(timelineOrderGroup);
							} else {
								timelineOrderGroup = existingOrder;
							}

							// employee => order => jobs
							// TODO: here could be subscontractor instead of employee
							const employeeOrderJob = employeesJobsSchedule.filter(_ => _?.employee?.id == employeeId && _.order?.id == orderId);

							if (!employeeOrderJob.length) {
								return;
							}

							timelineOrderGroup.slots = [];

							employeeOrderJob.forEach((j, jIndex) => { 
								if (!j.job) {
									return;
								}

								const orderJobId = j.job!.id;
								const serviceJobId = j.job!.serviceJob!.id;
								const assigneeId = j.orderJobAssigneeId;
								const timelineJob = new TimelineItemModel();
								timelineJob.id = (assigneeId + 1) * Math.sqrt(employeeId + eIndex + oIndex + jIndex);
								timelineJob.orderId = orderId;
								timelineJob.orderJobId = orderJobId;
								timelineJob.orderServiceJobId = serviceJobId;
								timelineJob.orderServiceJobAssigneeId = assigneeId;
								timelineJob.start = mapDate(j.scheduledDate, j.scheduledStartTime);
								timelineJob.end = mapDate(j.scheduledEndDate, j.scheduledEndTime);
								timelineJob.virtual = assigneeId == 0;
								timelineJob.subcontract = j.subcontract;

								// add job to chart-data array
								timelineOrderGroup.slots!.push(timelineJob);
							});
						});
					});

					// console.log('1.1', employeesJobsSchedule);
					// console.log('2.1', jobsSchedule);
				}

				// REMARK: instead of marking non-working hours with inactive-background limit timeline to bounds 
				// const nonWorkingHours = await jobsScheduleApiHelper.getNonWorkingHours(props.branch as number, store.getters.jwt);
				
				const 
					shiftStartHour = 9,
					shiftEndHour = 21;

				const timelineConfig : TimelineConfiguration = mapTimelineConfig(
					props.scheduleDate,
					jobsSchedule,
					shiftStartHour, 
					shiftEndHour
				);

				iframeState.value.status = 'loading'; // Still loading as we prepare iframe
				isIframeVisible.value = true;
				await nextTick();
				if (!iframeRef.value?.contentWindow) {
					throw new Error('Iframe element failed to initialize');
				}

				const iframe = iframeRef.value;
				const iframeDocument = iframe.contentDocument;
				if (iframeDocument?.readyState === 'complete') {
					sendMessage(iframe.contentWindow!, 'set', timelineConfig);
					iframeState.value.status = 'loaded';
				} else {
					iframe.addEventListener('load', () => {
						sendMessage(iframe.contentWindow!, 'set', timelineConfig);
						iframeState.value.status = 'loaded';
					});
					
					iframe.addEventListener('error', () => {
						iframeState.value = { status: 'error', error: 'Failed to load iframe content' };
					});
				}
			} catch (ex: any) {
				iframeState.value = { status: 'error', error: ex.message };
				console.error('error initializing iframe', ex);
			}
		};

		const destroyTimelineIframe = () => {
			removeTimelineEventListener();
			isIframeVisible.value = false;
			isInitialized.value = false;
		};

		const handleTimelineEvent = async (event: MessageEvent<ITimelineEvent>) => {
			const eventPayload = event.data;

			switch (eventPayload.type) {
				case 'item-updated': {					
					isLoading.value = true;
					const jobAssignee = mapTimelineItemModel(eventPayload.data);				
					let isJobScheduleSaved = false;
					
					if (!jobAssignee.virtual &&
						jobAssignee.orderJobAssigneeId) {
						isJobScheduleSaved = await saveScheduleAsync(jobAssignee);
					} else {
						isJobScheduleSaved = true;
					}
					isLoading.value = false;
					if (isJobScheduleSaved) {
						ElNotification({
							message: 'Изменения сохранены',
							type: 'success',
						});
						emit('item-updated', jobAssignee);
					} else {
						ElNotification({
							message: 'Ошибка сохранения интервала',
							type: 'error',
						});
					}
				}
				break;

				case 'item-clicked': {
					const jobAssignee = mapTimelineItemModel(eventPayload.data);	
					emit('item-clicked', jobAssignee);
				}
				break;

				case 'dimensions-updated': {
					iframeRef.value!.style.height = `${eventPayload.sizes.height}px`;		
				}
				break;
			}
		};

		const addTimelineEventListener = () => {
			window.addEventListener('message', handleTimelineEvent);
		};

		const removeTimelineEventListener = () => {
			window.removeEventListener('message', handleTimelineEvent);
		};

		const sendMessage = (contentWindowRef: Window, action: string, payload: any) => {
			const messagePayload = {
				type: 'timeline-message',
				action: action,
				data: payload,
				timetasmp: new Date().toISOString()
			};
			contentWindowRef.postMessage(messagePayload);
		};

		/** [obsolete mapping of EmployeeJobScheduleModel to TimelineGroupModel]
		const mapJobsSchedule = (employeesJobsSchedule: Array<EmployeeJobScheduleModel>): Array<TimelineGroupModel> => {			

			// 
			//   hashing of Timeline group ID
			//   generates a persistent number to avoid duplication of a single order if an employee were assiged to
			//
			const hashTimelineGroupId = (employeeJobSchedule: EmployeeJobScheduleModel) : number => {
				if (!employeeJobSchedule.virtual) {
					return (employeeJobSchedule.order!.id << 32) | employeeJobSchedule.employee!.id;			
				}			

				return Math.round(Math.sqrt(employeeJobSchedule.order!.id)) | employeeJobSchedule.employee!.id;
			}

			//
			// map EmployeeJobScheduleModel to VIS-native TimelineItemModel
			//
			const mapTimelineItem = (jobSchedule: EmployeeJobScheduleModel): TimelineItemModel => {
				const timelineItem = new TimelineItemModel();
				timelineItem.id = jobSchedule.orderJobAssigneeId;
				timelineItem.orderId = jobSchedule.order?.id ?? null;
				timelineItem.orderJobId = jobSchedule.job?.id ?? null;
				timelineItem.orderServiceJobId = jobSchedule.job?.serviceJob?.id ?? null;
				timelineItem.start = mapDate(jobSchedule.scheduledDate, jobSchedule.scheduledStartTime);
				timelineItem.end = mapDate(jobSchedule.scheduledDate, jobSchedule.scheduledEndTime);
				timelineItem.virtual = jobSchedule.virtual;
				return timelineItem;
			};

			const mapJobsSchedule = (jobsSchedule: Array<EmployeeJobScheduleModel>) => {
				const groupedByEmployee = new Array<TimelineGroupModel>();
				jobsSchedule.forEach(j => {
					const existingEmployeeGroup = groupedByEmployee.find(_ => _.id == j.employee!.id);
					if (existingEmployeeGroup) {
						const timelineItem = mapTimelineItem(j);
						const existingOrderGroup = existingEmployeeGroup.subGroups?.find(_ => _.id == hashTimelineGroupId(j));

						if (existingOrderGroup) {
							existingOrderGroup.slots!.push(timelineItem);
						} else {
							const govNumber = j.order!.vehicle?.govermentalNumber ?? '';
							if (!existingEmployeeGroup.subGroups) {
								existingEmployeeGroup.subGroups = [];
							}
							existingEmployeeGroup.subGroups.push(new TimelineGroupModel({
								id: hashTimelineGroupId(j),
								name: `${govNumber}${govNumber ? ', ' : ''} ${j.order!.vehicle!.model!.manufacturer!.name} ${j.order!.vehicle!.model!.name}`,
								slots: [timelineItem],
								virtualOrder: j.virtual
							}));
						}
					} else {
						if (j.order?.id) {
							const timelineItem = mapTimelineItem(j);
							const govNumber = j.order!.vehicle?.govermentalNumber ?? '';
							groupedByEmployee.push(new TimelineGroupModel({
								id: j.employee!.id,
								name: `${j.employee?.lastname} ${j.employee?.firstname}`,
								head: true,
								subGroups: [new TimelineGroupModel({
									id: hashTimelineGroupId(j),
									name: `${govNumber}${govNumber ? ', ' : ''} ${j.order!.vehicle!.model!.manufacturer!.name} ${j.order!.vehicle!.model!.name}`,
									slots: [timelineItem],
									virtualOrder: j.virtual
								})]
							}));
						} else {
							groupedByEmployee.push(new TimelineGroupModel({
								id: j.employee!.id,
								name: `${j.employee?.lastname} ${j.employee?.firstname}`,
								head: true
							}));
						}
					}
				});				
				return groupedByEmployee;
			};

			return mapJobsSchedule(employeesJobsSchedule);
		};
		*/

		const saveScheduleAsync = (jobSchedule: EmployeeJobScheduleModel) : Promise<boolean> => {
			return jobsScheduleApiHelper.saveJobSchedule(
				jobSchedule.orderJobAssigneeId,
				jobSchedule.scheduledDate,
				jobSchedule.scheduledStartTime,				
				jobSchedule.scheduledEndDate,
				jobSchedule.scheduledEndTime,
				store.getters.jwt
			);
		}

		const mapTimelineConfig = (
			date: Date,
			timelines: Array<TimelineGroupModel>,
			shiftStartHour: number,
			shiftEndHour: number
		): TimelineConfiguration => {
			return new TimelineConfiguration({
				date,
				width: '100%',
				shiftStartHour,
				shiftEndHour,
				timelines
			});
		};

		/**
		 * map dateOnly + timeOnly to Date object
		 */
		const mapDate = (dateOnly: string, timeOnly: string): Date => {
			const [yearStr, monthStr, dayStr] = dateOnly.split('-');
			const [hourStr, minuteStr, secondStr] = timeOnly.split(':');

			const year = +yearStr,
				month = (+monthStr) - 1,
				day = +dayStr;

			const hour = +hourStr,
				minute = +minuteStr,
				second = +secondStr;

			return new Date(year, month, day, hour, minute, second);
		};

		const mapTimelineItemModel = (timelineItem: ITimelineItem) : EmployeeJobScheduleModel => {
			const scheduledDateOnly = dateOnlyHelper.date(timelineItem.start);
			const scheduledEndDateOnly = dateOnlyHelper.date(timelineItem.end);
			const startTimeOnly = timeOnlyHelper.time(timelineItem.start);
			const endTimeOnly = timeOnlyHelper.time(timelineItem.end);
			
			const employeeJobSchedule: EmployeeJobScheduleModel = {
				allocationPercent: 0,
				orderJobAssigneeId: timelineItem.serviceJobAssigneeId,
				scheduledDate: scheduledDateOnly,
				scheduledStartTime: startTimeOnly,
				scheduledEndTime: endTimeOnly,
				scheduledEndDate: scheduledEndDateOnly,
				isDisabled: false,
				employee: new EmployeeModel({
					id: timelineItem.employeeId
				}),
				order: new OrderModel({
					id: timelineItem.orderId
				}),
				job: new OrderJobModel({
					id: timelineItem.orderJobId,
					serviceJob: {
						id: timelineItem.serviceJobId
					}
				}),
				virtual: timelineItem.virtual,
				subcontract: timelineItem.subcontract
			};

			return employeeJobSchedule;
		}

		// Expose to template
		return {
			isLoading,
			isInitialized,
			isIframeVisible,
			iframeRef,
			iframeState
		};
	}
});
</script>

<style scoped>
.timeline-container {

}
	.timeline-container iframe {
		border: 0;
	}
</style>